'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { theme } from "@/styles/theme";

interface AdminViewProps {
    onBack: () => void;
}

interface InlineButton {
    text: string;
    url: string;
}

export default function AdminView({ onBack }: AdminViewProps) {
    const [stats, setStats] = React.useState<{ total: number, today: number, month: number } | null>(null);
    const [loadingStats, setLoadingStats] = React.useState(true);
    
    const [message, setMessage] = React.useState('');
    const [target, setTarget] = React.useState('single');
    const [targetId, setTargetId] = React.useState('877956556');
    const [buttons, setButtons] = React.useState<InlineButton[]>([]);
    const [sending, setSending] = React.useState(false);
    
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');

    React.useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await fetch('/admin/stats');
                if (res.ok) {
                    const data = await res.json();
                    setStats(data);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoadingStats(false);
            }
        };
        fetchStats();
    }, []);

    const handleAddButton = () => {
        setButtons([...buttons, { text: '', url: '' }]);
    };

    const handleButtonChange = (index: number, field: keyof InlineButton, value: string) => {
        const newButtons = [...buttons];
        newButtons[index][field] = value;
        setButtons(newButtons);
    };

    const handleDeleteButton = (index: number) => {
        const newButtons = buttons.filter((_, i) => i !== index);
        setButtons(newButtons);
    };

    const handleSend = async () => {
        if (!message) return;
        
        // Фильтруем пустые кнопки
        const validButtons = buttons.filter(b => b.text && b.url);

        setSending(true);
        try {
            const res = await fetch('/admin/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    target,
                    message,
                    telegramId: target === 'single' ? targetId : undefined,
                    buttons: validButtons
                })
            });
            
            const data = await res.json();
            if (data.success) {
                setSnackbarMessage(`Отправлено: ${data.sent}, Ошибок: ${data.failed}`);
                setMessage('');
                setButtons([]);
            } else {
                setSnackbarMessage(`Ошибка: ${data.error}`);
            }
        } catch (e) {
            setSnackbarMessage('Ошибка сети');
        } finally {
            setSending(false);
            setSnackbarOpen(true);
        }
    };

    const StatCard = ({ title, value }: { title: string, value: number }) => (
        <Paper sx={{ 
            p: 2, 
            flex: 1, 
            background: "linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
            backdropFilter: "blur(10px)",
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 1 
        }}>
            <Typography variant="caption" color="text.secondary">{title}</Typography>
            <Typography variant="h5" fontWeight="bold">{value} ₽</Typography>
        </Paper>
    );

    const HEADER_HEIGHT = 72;

    return (
        <Box sx={{ 
            position: 'fixed', 
            inset: 0, 
            background: "linear-gradient(rgb(10, 10, 10) 0%, rgb(26, 26, 26) 50%, rgb(10, 10, 10) 100%)",
            zIndex: 9999,
            overflow: 'hidden',
            overscrollBehavior: 'none',
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
        }}>
            {/* Header */}
            <Box sx={{
                height: `${HEADER_HEIGHT}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                bgcolor: 'transparent',
                zIndex: 1001,
                flexShrink: 0,
            }}>
                <Typography variant="h6" fontWeight="bold">Админ панель</Typography>
                <IconButton onClick={onBack} sx={{backgroundColor:theme.palette.background.paper}}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ 
                flexGrow: 1,
                overflowY: 'auto',
                width: '100%',
                px: 2,
                pb: 4,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '3px' }
            }}>
                <Stack spacing={3} maxWidth={800} mx="auto">
                    {/* Статистика */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2} color="text.secondary">Финансы</Typography>
                        {loadingStats ? (
                            <Box display="flex" justifyContent="center"><CircularProgress /></Box>
                        ) : stats ? (
                            <Stack spacing={2}>
                                <StatCard title="За все время" value={stats.total} />
                                <Stack direction="row" spacing={2}>
                                    <StatCard title="За сегодня" value={stats.today} />
                                    <StatCard title="За месяц" value={stats.month} />
                                </Stack>
                            </Stack>
                        ) : (
                            <Typography color="error">Не удалось загрузить статистику</Typography>
                        )}
                    </Box>

                    {/* Рассылка */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2} color="text.secondary">Рассылка</Typography>
                        <Paper sx={{ 
                            p: 2, 
                            borderRadius: 1,
                            background: "linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
                            backdropFilter: "blur(10px)",
                            border: '1px solid rgba(255, 255, 255, 0.12)',
                        }}>
                            <Stack spacing={2}>
                                <ToggleButtonGroup
                                    value={target}
                                    exclusive
                                    onChange={(_, newTarget) => newTarget && setTarget(newTarget)}
                                    fullWidth
                                    size="small"
                                    sx={{ 
                                        bgcolor: 'rgba(0,0,0,0.2)', 
                                        borderRadius: 1,
                                        '& .MuiToggleButton-root': {
                                            color: 'text.secondary',
                                            border: 'none',
                                            '&.Mui-selected': {
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                '&:hover': { bgcolor: 'primary.dark' }
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value="single">Одному</ToggleButton>
                                    <ToggleButton value="all">Всем</ToggleButton>
                                </ToggleButtonGroup>

                                {target === 'single' && (
                                    <TextField
                                        label="Telegram ID"
                                        value={targetId}
                                        onChange={(e) => setTargetId(e.target.value)}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        sx={{ 
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: 'rgba(0,0,0,0.2)',
                                            }
                                        }}
                                    />
                                )}

                                <TextField
                                    label="Сообщение (HTML)"
                                    multiline
                                    rows={4}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    sx={{ 
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'rgba(0,0,0,0.2)',
                                        }
                                    }}
                                />

                                {/* Кнопки */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary" mb={1} display="block">Кнопки (опционально)</Typography>
                                    <Stack spacing={1}>
                                        {buttons.map((btn, index) => (
                                            <Stack key={index} direction="row" spacing={1} alignItems="center">
                                                <TextField
                                                    placeholder="Текст"
                                                    value={btn.text}
                                                    onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.2)' } }}
                                                />
                                                <TextField
                                                    placeholder="URL"
                                                    value={btn.url}
                                                    onChange={(e) => handleButtonChange(index, 'url', e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.2)' } }}
                                                />
                                                <IconButton onClick={() => handleDeleteButton(index)} size="small" color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>
                                        ))}
                                        <Button 
                                            startIcon={<AddIcon />} 
                                            onClick={handleAddButton} 
                                            size="small" 
                                            sx={{ alignSelf: 'flex-start' }}
                                        >
                                            Добавить кнопку
                                        </Button>
                                    </Stack>
                                </Box>

                                <Button
                                    variant="contained"
                                    onClick={handleSend}
                                    disabled={sending || !message}
                                    fullWidth
                                    size="large"
                                >
                                    {sending ? <CircularProgress size={24} color="inherit" /> : 'Отправить'}
                                </Button>
                            </Stack>
                        </Paper>
                    </Box>
                </Stack>
            </Box>
            
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}
